import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, onSnapshot, orderBy, query, where, limit, startAfter, getDocs, getAggregateFromServer, sum as fbSum, count as fbCount } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useCompany } from '../context/CompanyContext';
import { Copy, Send, DollarSign, CheckCircle2, Clock, Receipt, CreditCard, RotateCcw, Zap, X, AlertCircle, AlertTriangle, CheckSquare, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';

import { toast } from 'sonner';
import ChargeNowModal from '../components/ChargeNowModal';
import MarkPaidModal, { MANUAL_PAYMENT_METHODS } from '../components/MarkPaidModal';
import RefundModal from '../components/RefundModal';
import { useRole } from '../context/RoleContext';
import { isOutstanding, OWED_STATUSES } from '../lib/invoiceFinancials';
import { agingCutoffs, reconcileAgingResidual } from '../lib/invoiceAging';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt$ = (n) => `$${parseFloat(n || 0).toFixed(2)}`;

// Normalize Firestore Timestamp or ISO string to a JS Date
const toDate = (val) => {
  if (!val) return null;
  if (val?.toDate) return val.toDate();       // Firestore Timestamp
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
};

const fmtDate = (val) => {
  const d = toDate(val);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const shortId = (id) => (id ? id.slice(-8).toUpperCase() : '—');

// Days since a date (for aging)
const daysOld = (val) => {
  const d = toDate(val);
  if (!d) return 0;
  return Math.floor((Date.now() - d.getTime()) / 86_400_000);
};

const ageBucket = (days) => {
  if (days >= 90) return { label: `${days}d`, cls: 'bg-red-100 text-red-700 border-red-200' };
  if (days >= 60) return { label: `${days}d`, cls: 'bg-orange-100 text-orange-700 border-orange-200' };
  if (days >= 30) return { label: `${days}d`, cls: 'bg-amber-100 text-amber-700 border-amber-200' };
  return null;
};

const AGING_LABELS = { current: 'Current', d30: '30–60d', d60: '60–90d', d90: '90d+' };

const isThisMonth = (val) => {
  const d = toDate(val);
  if (!d) return false;
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
};

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_META = {
  paid:               { label: 'Paid',        cls: 'bg-green-100 text-green-700' },
  sent:               { label: 'Sent',        cls: 'bg-blue-100  text-blue-700'  },
  unpaid:             { label: 'Unpaid',      cls: 'bg-amber-100 text-amber-700' },
  draft:              { label: 'Draft',       cls: 'bg-gray-100  text-gray-600'  },
  failed_checkout:    { label: 'Failed',      cls: 'bg-red-100   text-red-700'   },
  refunded:           { label: 'Refunded',    cls: 'bg-orange-100 text-orange-700' },
  partially_refunded: { label: 'Partial Refund', cls: 'bg-orange-50 text-orange-700' },
  // In-flight / terminal non-receivable states — shown distinctly (NOT "Draft").
  pending:            { label: 'Processing',  cls: 'bg-gray-100 text-gray-500' },
  pending_intent:     { label: 'Processing',  cls: 'bg-gray-100 text-gray-500' },
  initiated:          { label: 'Processing',  cls: 'bg-gray-100 text-gray-500' },
  void:               { label: 'Void',        cls: 'bg-gray-100 text-gray-400' },
  canceled:           { label: 'Canceled',    cls: 'bg-gray-100 text-gray-400' },
  cancelled:          { label: 'Canceled',    cls: 'bg-gray-100 text-gray-400' },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] ?? STATUS_META.draft;
  return (
    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${meta.cls}`}>
      {meta.label}
    </span>
  );
};

// ─── Aging Strip ──────────────────────────────────────────────────────────────

// Aging is driven by EXACT server-side aggregates (count + sum) per `createdAt`
// range — NOT the bounded visible list. `buckets` is { current, d30, d60, d90 }
// where each value is { count, sum } (or null if that aggregate is unavailable).
// `available` is true only when all four aggregates resolved. `residual` is the
// reconciliation guardrail: owed invoices that lack a valid Timestamp createdAt
// (never silently hidden).
const AgingStrip = ({ buckets, available, loaded, residual, activeFilter, onBucketClick }) => {
  const meta = [
    { key: 'current', label: 'Current',   color: 'text-gray-600',   bg: 'bg-gray-100',  ring: 'ring-gray-400'   },
    { key: 'd30',     label: '30–60d',    color: 'text-amber-700',  bg: 'bg-amber-50',  ring: 'ring-amber-400'  },
    { key: 'd60',     label: '60–90d',    color: 'text-orange-700', bg: 'bg-orange-50', ring: 'ring-orange-400' },
    { key: 'd90',     label: '90d+',      color: 'text-red-700',    bg: 'bg-red-50',    ring: 'ring-red-400'    },
  ];
  const b = buckets || {};
  const overdueCount = available
    ? (b.d30?.count || 0) + (b.d60?.count || 0) + (b.d90?.count || 0)
    : 0;

  return (
    <div
      className="bg-white border border-gray-200 rounded-xl px-4 py-3"
      data-testid="aging-strip"
    >
      <div className="flex items-center gap-2 mb-2.5">
        <Clock className="h-3.5 w-3.5 text-gray-400" />
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Invoice Aging</span>
        {available && overdueCount === 0 && !activeFilter && (
          <span className="text-xs text-green-600 font-medium ml-1">All current</span>
        )}
        {activeFilter && (
          <button
            onClick={() => onBucketClick(activeFilter)}
            className="ml-auto flex items-center gap-1 text-[11px] font-medium text-gray-400 hover:text-gray-700 transition-colors"
            data-testid="aging-clear-filter"
          >
            <X className="h-3 w-3" />
            Clear filter
          </button>
        )}
      </div>

      {!available ? (
        <p className="text-xs text-gray-400 py-1" data-testid="aging-unavailable">
          {loaded
            ? 'Invoice aging is unavailable — a Firestore index is pending deployment.'
            : 'Calculating invoice aging…'}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-2">
            {meta.map(m => {
              const data = b[m.key] || { count: 0, sum: 0 };
              const isEmpty = data.count === 0;
              const isActive = activeFilter === m.key;
              return (
                <button
                  key={m.key}
                  type="button"
                  data-testid={`aging-bucket-${m.key}`}
                  onClick={() => !isEmpty && onBucketClick(m.key)}
                  disabled={isEmpty}
                  className={[
                    'rounded-lg px-3 py-2 border text-left transition-all duration-150',
                    isEmpty
                      ? 'border-gray-100 bg-gray-50 cursor-default'
                      : `${m.bg} hover:brightness-95 cursor-pointer`,
                    isActive
                      ? `ring-2 ring-offset-1 ${m.ring} shadow-sm border-transparent`
                      : 'border-current/10',
                  ].join(' ')}
                >
                  <p className={`text-[10px] font-semibold uppercase tracking-wide mb-0.5 ${isEmpty ? 'text-gray-300' : m.color}`}>
                    {m.label}
                  </p>
                  <p
                    className={`text-sm font-bold ${isEmpty ? 'text-gray-300' : m.color}`}
                    data-testid={`aging-bucket-${m.key}-amount`}
                  >
                    {isEmpty ? '—' : `$${(data.sum || 0).toFixed(2)}`}
                  </p>
                  {!isEmpty && (
                    <p className={`text-[10px] ${m.color} opacity-70`}>
                      {data.count} inv{data.count !== 1 ? 's' : ''}
                    </p>
                  )}
                </button>
              );
            })}
          </div>
          {residual && residual.count > 0 && (
            <p
              className="text-[11px] text-gray-500 mt-2 flex items-center gap-1"
              data-testid="aging-residual"
            >
              <AlertTriangle className="h-3 w-3 text-gray-400" />
              {residual.count} invoice{residual.count !== 1 ? 's' : ''}
              {residual.sum != null ? ` · $${residual.sum.toFixed(2)}` : ''} unaged
              <span className="text-gray-400">(missing a valid date)</span>
            </p>
          )}
        </>
      )}
    </div>
  );
};

// ─── Payment-method label helper (manual + Stripe paths) ─────────────────────

const PAYMENT_METHOD_LABEL = (() => {
  const map = {};
  MANUAL_PAYMENT_METHODS.forEach(m => { map[m.value] = m.label; });
  map.card_on_file = 'Auto-charged';
  return map;
})();

// ─── Invoice Row ─────────────────────────────────────────────────────────────

const InvoiceRow = ({
  invoice,
  onCopyLink, onSendReminder, sending,
  onRetryCharge, retrying,
  onRetryInvoice, retryingInvoice,
  onChargeNow,
  onMarkPaid,
  onRefund,
  canMarkPaid,
}) => {
  const isPaid    = invoice.status === 'paid';
  const isFullyRefunded     = invoice.status === 'refunded';
  const isPartiallyRefunded = invoice.status === 'partially_refunded';
  const canRetry  = invoice.autoChargeFailed && !isPaid && !isFullyRefunded && !isPartiallyRefunded;
  // Recover a failed-checkout stub: no payment link was ever created.
  const isFailedCheckout = invoice.status === 'failed_checkout' || invoice.checkoutCreationFailed === true;
  // "Charge Now" for any unpaid, non-failed, non-prepay invoice with a payment link.
  const canCharge = !isPaid && !canRetry && !isFailedCheckout
    && !isFullyRefunded && !isPartiallyRefunded
    && invoice.invoiceType !== 'prepay';
  // "Mark Paid" surfaces for any unpaid invoice when the operator has the role
  // (owner/admin). Hidden on paid + prepay (prepay is collected via Stripe only).
  const showMarkPaid = canMarkPaid && !isPaid
    && !isFullyRefunded && !isPartiallyRefunded
    && invoice.invoiceType !== 'prepay';
  // "Refund" surfaces for paid OR partially-refunded invoices with remaining
  // refundable balance, owner/admin only.
  const paidAmt = parseFloat(invoice.amount || invoice.grandTotal || 0);
  const refundedAmt = parseFloat(invoice.refundedAmount || 0);
  const refundable = Math.max(0, +(paidAmt - refundedAmt).toFixed(2));
  const showRefund = canMarkPaid
    && (isPaid || isPartiallyRefunded)
    && refundable >= 0.01;
  const isRetrying = retrying === invoice.invoiceId;
  const isRetryingInvoice = retryingInvoice === invoice.invoiceId;

  // Manual mark-paid label surface (e.g. "Check · #1234")
  const manualLabel = invoice.paymentMethodLabel
    || (invoice.paymentMethod ? PAYMENT_METHOD_LABEL[invoice.paymentMethod] : '');
  const manualRef   = (invoice.paymentReference || '').trim();

  return (
    <div
      className="flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-3 px-4 py-3 border-b last:border-0 hover:bg-gray-50 transition-colors"
      data-testid={`invoice-row-${invoice.invoiceId}`}
    >
      {/* Left: customer + ID (row 1 on mobile) */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm text-gray-900 truncate">
          {invoice.customerName || 'Unknown'}
        </p>
        <p className="text-[11px] text-gray-400 font-mono">#{shortId(invoice.invoiceId)}</p>
      </div>

      {/* Amount (row 1 right on mobile — visually paired with customer) */}
      <div className="shrink-0 text-right w-20 lg:w-20 order-none lg:order-none">
        <p className="font-semibold text-sm text-gray-900">{fmt$(invoice.amount)}</p>
      </div>

      {/* Status */}
      <div className="shrink-0 w-16 flex justify-center">
        <StatusBadge status={invoice.status} />
      </div>

      {/* Dates + payment method */}
      <div className="shrink-0 text-right w-28 hidden sm:block">
        <p className="text-xs text-gray-500">{fmtDate(invoice.createdAt)}</p>
        {isPaid && invoice.paidAt && (
          <p className="text-[10px] text-green-600">Paid {fmtDate(invoice.paidAt)}</p>
        )}
        {!isPaid && (() => {
          const days = daysOld(invoice.createdAt);
          const bucket = ageBucket(days);
          return bucket ? (
            <span
              className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full border mt-0.5 ${bucket.cls}`}
              data-testid={`aging-badge-${invoice.invoiceId}`}
            >
              {bucket.label}
            </span>
          ) : null;
        })()}
        {isPaid && invoice.paymentMethod === 'card_on_file' && (
          <p className="text-[10px] text-blue-600 flex items-center justify-end gap-0.5 mt-0.5">
            <CreditCard className="h-2.5 w-2.5" />
            Auto-charged
          </p>
        )}
        {isPaid && invoice.manualMarkPaid && manualLabel && (
          <p
            className="text-[10px] text-green-700 flex items-center justify-end gap-0.5 mt-0.5"
            data-testid={`manual-paid-label-${invoice.invoiceId}`}
            title={manualRef ? `Ref: ${manualRef}` : undefined}
          >
            <CheckSquare className="h-2.5 w-2.5" />
            {manualLabel}{manualRef ? ` · ${manualRef.length > 16 ? manualRef.slice(0, 14) + '…' : manualRef}` : ''}
          </p>
        )}
        {isPaid && invoice.discountOnly && (
          <p
            className="text-[10px] text-amber-700 flex items-center justify-end gap-0.5 mt-0.5"
            data-testid={`discount-only-label-${invoice.invoiceId}`}
            title="Invoice waived entirely by one-time discount"
          >
            <CheckSquare className="h-2.5 w-2.5" />
            One-Time Discount
          </p>
        )}
        {(invoice.oneTimeDiscountCents || 0) > 0 && !invoice.discountOnly && (
          <p
            className="text-[10px] text-amber-700 flex items-center justify-end gap-0.5 mt-0.5"
            data-testid={`discount-applied-label-${invoice.invoiceId}`}
            title={invoice.oneTimeDiscountReason || 'One-time discount applied'}
          >
            <CheckSquare className="h-2.5 w-2.5" />
            -${((invoice.oneTimeDiscountCents || 0) / 100).toFixed(2)} discount
          </p>
        )}
        {isPaid && invoice.creditOnly && (
          <p
            className="text-[10px] text-indigo-700 flex items-center justify-end gap-0.5 mt-0.5"
            data-testid={`credit-only-label-${invoice.invoiceId}`}
            title="Invoice paid entirely from account credit"
          >
            <CheckSquare className="h-2.5 w-2.5" />
            Account Credit
          </p>
        )}
        {(invoice.creditAppliedCents || 0) > 0 && !invoice.creditOnly && (
          <p
            className="text-[10px] text-indigo-700 flex items-center justify-end gap-0.5 mt-0.5"
            data-testid={`credit-applied-label-${invoice.invoiceId}`}
            title={`$${((invoice.creditAppliedCents || 0) / 100).toFixed(2)} credit applied`}
          >
            <CheckSquare className="h-2.5 w-2.5" />
            +${((invoice.creditAppliedCents || 0) / 100).toFixed(2)} credit
          </p>
        )}
        {(invoice.creditRestoredCents || 0) > 0 && (
          <p
            className="text-[10px] text-emerald-700 flex items-center justify-end gap-0.5 mt-0.5"
            data-testid={`credit-restored-label-${invoice.invoiceId}`}
            title={`$${((invoice.creditRestoredCents || 0) / 100).toFixed(2)} credit restored from refund`}
          >
            <RotateCcw className="h-2.5 w-2.5" />
            +${((invoice.creditRestoredCents || 0) / 100).toFixed(2)} credit restored
          </p>
        )}
        {(isFullyRefunded || isPartiallyRefunded || refundedAmt > 0) && (
          <p
            className="text-[10px] text-orange-700 flex items-center justify-end gap-0.5 mt-0.5"
            data-testid={`refund-label-${invoice.invoiceId}`}
            title={`Refunded $${refundedAmt.toFixed(2)} of $${paidAmt.toFixed(2)}`}
          >
            <RotateCcw className="h-2.5 w-2.5" />
            {isFullyRefunded
              ? `Refunded $${refundedAmt.toFixed(2)}`
              : `Refunded $${refundedAmt.toFixed(2)} / $${paidAmt.toFixed(2)}`}
          </p>
        )}
        {canRetry && (
          <p className="text-[10px] text-red-500 mt-0.5" data-testid={`charge-failed-label-${invoice.invoiceId}`}>
            Charge failed
          </p>
        )}
        {invoice.autoChargeSkipped && !canRetry && !isPaid && (
          <p
            className="text-[10px] text-amber-600 mt-0.5"
            data-testid={`autocharge-skipped-label-${invoice.invoiceId}`}
            title={
              `Auto-charge was skipped (${invoice.autoChargeSkipReason || 'lifecycle'}). ` +
              'The payment link is still active and the operator can charge manually.'
            }
          >
            Auto-charge skipped
          </p>
        )}
        {isFailedCheckout && !canRetry && (
          <p
            className="text-[10px] text-red-500 mt-0.5"
            data-testid={`checkout-failed-label-${invoice.invoiceId}`}
            title={invoice.lastFailureReason || 'Payment link could not be created.'}
          >
            Invoice failed
          </p>
        )}
      </div>

      {/* Actions — wrap on mobile, single-row on desktop */}
      <div className="flex flex-wrap items-center gap-1 lg:shrink-0 lg:flex-nowrap w-full lg:w-auto">
        {/* Retry Invoice — recover a failed-checkout stub (no payment link yet) */}
        {isFailedCheckout && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50 hover:border-red-400 font-medium"
            onClick={() => onRetryInvoice(invoice)}
            disabled={isRetryingInvoice}
            data-testid={`retry-invoice-btn-${invoice.invoiceId}`}
            title={invoice.lastFailureReason
              ? `Retry invoice (last failure: ${invoice.lastFailureReason.slice(0, 120)})`
              : 'Retry invoice — recreate the Stripe payment link'}
          >
            <RotateCcw className={`h-3.5 w-3.5 ${isRetryingInvoice ? 'animate-spin' : ''} mr-1`} />
            {isRetryingInvoice ? 'Retrying…' : 'Retry Invoice'}
          </Button>
        )}

        {/* Retry Charge — only when autoChargeFailed and not yet paid */}
        {canRetry && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50 hover:border-red-400 font-medium"
            onClick={() => onRetryCharge(invoice)}
            disabled={isRetrying}
            data-testid={`retry-charge-btn-${invoice.invoiceId}`}
            title="Retry charge on saved card"
          >
            <RotateCcw className={`h-3.5 w-3.5 ${isRetrying ? 'animate-spin' : ''} mr-1`} />
            {isRetrying ? 'Charging…' : 'Retry'}
          </Button>
        )}

        {/* Charge Now — universal trigger for unpaid non-failed invoices */}
        {canCharge && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs text-green-700 border-green-300 hover:bg-green-50 hover:border-green-500 font-medium"
            onClick={() => onChargeNow(invoice)}
            data-testid={`charge-now-btn-${invoice.invoiceId}`}
            title="Charge Now — smart payment collection"
          >
            <Zap className="h-3.5 w-3.5 mr-1" />
            Charge
          </Button>
        )}

        {/* Mark Paid — owner/admin only, captures out-of-band payment */}
        {showMarkPaid && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-400 font-medium"
            onClick={() => onMarkPaid(invoice)}
            data-testid={`mark-paid-btn-${invoice.invoiceId}`}
            title="Mark Paid — record a check / cash / external payment"
          >
            <CheckSquare className="h-3.5 w-3.5 mr-1" />
            Mark Paid
          </Button>
        )}

        {/* Refund — owner/admin only, paid/partially-refunded with balance */}
        {showRefund && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs text-orange-700 border-orange-200 hover:bg-orange-50 hover:border-orange-400 font-medium"
            onClick={() => onRefund(invoice)}
            data-testid={`refund-btn-${invoice.invoiceId}`}
            title={`Refund up to ${`$${refundable.toFixed(2)}`}`}
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1" />
            Refund
          </Button>
        )}

        {/* Payment link actions (copy + SMS reminder) */}
        {!isPaid && invoice.stripePaymentLink && (
          <>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs text-gray-500 hover:text-gray-800"
              onClick={() => onCopyLink(invoice)}
              data-testid={`copy-link-btn-${invoice.invoiceId}`}
              title="Copy payment link"
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs text-gray-500 hover:text-[var(--cb-orange)]"
              onClick={() => onSendReminder(invoice)}
              disabled={sending === invoice.invoiceId}
              data-testid={`send-reminder-btn-${invoice.invoiceId}`}
              title="Send payment reminder via SMS"
            >
              <Send className="h-3.5 w-3.5" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Section ──────────────────────────────────────────────────────────────────
// Dumb list renderer — the parent passes an already page-sliced array (≤ 25
// rows). Row DOM is never allowed to grow beyond what's passed in.
const InvoiceSection = ({ title, icon: Icon, iconCls, invoices, emptyMsg, count, ...rowProps }) => (
  <div>
    <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border-b">
      <Icon className={`h-4 w-4 ${iconCls}`} />
      <h2 className="text-xs font-semibold text-gray-600 uppercase tracking-wider">{title}</h2>
      {count != null && (
        <span className="text-[10px] bg-white border text-gray-500 rounded-full px-2 py-0.5">{count}</span>
      )}
    </div>
    {invoices.length === 0 ? (
      <p className="px-4 py-6 text-sm text-gray-400 text-center">{emptyMsg}</p>
    ) : (
      invoices.map(inv => <InvoiceRow key={inv.invoiceId} invoice={inv} {...rowProps} />)
    )}
  </div>
);

// ─── Pagination footer ──────────────────────────────────────────────────────
// Bounded Previous · Page N · Next with a record range. Navigating REPLACES the
// visible rows (parent slices), so the list never grows beyond one page.
const PaginationBar = ({ page, pageSize, renderedCount, total, hasNext, hasPrev, onPrev, onNext, loading }) => {
  if (!hasNext && !hasPrev && page === 0) return null; // single page — no chrome
  const start = renderedCount === 0 ? 0 : page * pageSize + 1;
  const end = page * pageSize + renderedCount;
  const rangeLabel = renderedCount === 0
    ? '0'
    : (total != null ? `${start}–${end} of ${total}` : `${start}–${end}`);
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-gray-100 bg-gray-50/50" data-testid="invoice-pagination">
      <span className="text-[11px] text-gray-500" data-testid="invoice-pagination-range">{rangeLabel}</span>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={onPrev}
          disabled={!hasPrev || loading}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-gray-200 bg-white text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="invoice-page-prev"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          Previous
        </button>
        <span className="text-xs font-medium text-gray-700 px-1.5" data-testid="invoice-page-indicator">Page {page + 1}</span>
        <button
          type="button"
          onClick={onNext}
          disabled={!hasNext || loading}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-gray-200 bg-white text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="invoice-page-next"
        >
          Next
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const Invoices = () => {
  const { companyId } = useCompany();
  const { isOwner, isAdmin } = useRole();
  const canMarkPaid = isOwner || isAdmin;
  const [searchParams, setSearchParams] = useSearchParams();
  const attentionOnly = searchParams.get('filter') === 'attention';
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);   // invoiceId currently being SMS-sent
  const [retrying, setRetrying] = useState(null); // invoiceId currently being retried (auto-charge)
  const [retryingInvoice, setRetryingInvoice] = useState(null); // invoiceId currently re-creating checkout
  const [chargeNowInvoice, setChargeNowInvoice] = useState(null); // invoice open in ChargeNowModal
  const [markPaidInvoice, setMarkPaidInvoice] = useState(null);   // invoice open in MarkPaidModal
  const [refundInvoice, setRefundInvoice] = useState(null);       // invoice open in RefundModal
  const [agingFilter, setAgingFilter] = useState(null); // null = all | 'current' | 'd30' | 'd60' | 'd90'
  // Phase E: opt-in filter for invoices whose auto-charge was skipped by the
  // lifecycle gate (paused/closed accounts). Default OFF — full list still
  // includes these invoices, simply badged.
  const [skippedOnly, setSkippedOnly] = useState(false);
  // Invoices P2 — primary view tabs (default Outstanding). Only the active
  // view's list renders, so Paid history no longer stacks below Outstanding.
  const [view, setView] = useState('outstanding'); // 'outstanding' | 'paid' | 'all'
  // Local "Needs attention" toggle inside the Outstanding view (in addition to
  // the Dashboard ?filter=attention deep-link) — isolates failed-payment invoices.
  const [attentionToggle, setAttentionToggle] = useState(false);
  // Bounded pagination — 25 rendered rows max per page, Previous/Next replace
  // (never append). `page` resets on tab/filter change (effect below).
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(0);

  // ── Bounded, canonical invoice loading ──────────────────────────────────
  // Outstanding = canonical OWED set (sent/draft/failed_checkout), bounded +
  // "load more". Business-wide AR $ and count come from an EXACT server-side
  // aggregate (type-agnostic — no createdAt range — so mixed timestamp types
  // never break it). The visible list never needs every invoice in memory.
  const INVOICES_PAGE_SIZE = 200;
  const OWED_PAGE_SIZE = 100;
  const [olderInvoices, setOlderInvoices] = useState([]);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const lastHistoryDocRef = useRef(null);

  const [owedInvoices, setOwedInvoices] = useState([]);
  const [olderOwed, setOlderOwed] = useState([]);
  const [loadingMoreOwed, setLoadingMoreOwed] = useState(false);
  const [hasMoreOwed, setHasMoreOwed] = useState(false);
  const lastOwedDocRef = useRef(null);

  // EXACT business-wide receivable (independent of the visible list).
  //   • count()      → single-field status index (always available).
  //   • sum('amount') → (status, amount) composite index. If that index is not
  //     deployed the sum is reported as UNAVAILABLE (null). We NEVER substitute
  //     a partial loaded-page subtotal as the business-wide Outstanding dollars.
  // This total is TYPE-AGNOSTIC (no createdAt range) so a malformed/missing
  // timestamp can never drop a valid receivable from the company-wide total.
  const [arTotal, setArTotal] = useState({ count: null, sum: null, loaded: false });

  const loadArAggregate = async (cid) => {
    const base = query(collection(db, 'companies', cid, 'invoices'), where('status', 'in', OWED_STATUSES));
    let c = null, s = null;
    try { c = (await getAggregateFromServer(base, { count: fbCount() })).data().count; }
    catch (err) { console.warn('[Invoices] AR count aggregate unavailable:', err?.code || err); }
    try { s = (await getAggregateFromServer(base, { sum: fbSum('amount') })).data().sum; }
    catch (err) { console.warn('[Invoices] AR sum aggregate needs a (status, amount) index — reported unavailable (no partial fallback):', err?.code || err); }
    setArTotal({ count: c, sum: s, loaded: true });
  };

  // EXACT aging — a time-based split of the receivable via server aggregates on
  // `createdAt` ranges, scoped to canonical OWED statuses. Buckets are MUTUALLY
  // EXCLUSIVE and COLLECTIVELY EXHAUSTIVE for valid Timestamp records:
  //   current: age <30d | d30: 30–60 | d60: 60–90 | d90: 90+
  // Each range aggregate needs a (status, createdAt) composite index; when
  // unavailable the strip shows an honest index-pending state (never a partial
  // loaded set). Records with a malformed/missing Timestamp createdAt fall
  // outside every range and surface via the reconciliation residual below.
  const [agingAgg, setAgingAgg] = useState({ loaded: false, ok: false, buckets: null });

  const loadAgingAggregates = async (cid) => {
    const invRef = collection(db, 'companies', cid, 'invoices');
    const { cut30, cut60, cut90 } = agingCutoffs(Date.now());
    // createdAt boundaries (age = now − createdAt):
    //   current: createdAt >  cut30           → age < 30
    //   d30:     cut60 < createdAt <= cut30    → 30 ≤ age < 60
    //   d60:     cut90 < createdAt <= cut60    → 60 ≤ age < 90
    //   d90:     createdAt <= cut90            → age ≥ 90
    const specs = {
      current: [where('createdAt', '>', cut30)],
      d30:     [where('createdAt', '<=', cut30), where('createdAt', '>', cut60)],
      d60:     [where('createdAt', '<=', cut60), where('createdAt', '>', cut90)],
      d90:     [where('createdAt', '<=', cut90)],
    };
    const buckets = {};
    let ok = true;
    for (const [key, extra] of Object.entries(specs)) {
      try {
        const q = query(invRef, where('status', 'in', OWED_STATUSES), ...extra);
        const r = await getAggregateFromServer(q, { count: fbCount(), sum: fbSum('amount') });
        buckets[key] = { count: r.data().count, sum: r.data().sum };
      } catch (err) {
        console.warn(`[Invoices] aging aggregate '${key}' needs a (status, createdAt) index — reported unavailable:`, err?.code || err);
        ok = false;
        buckets[key] = null;
      }
    }
    setAgingAgg({ loaded: true, ok, buckets });
  };

  useEffect(() => {
    if (!companyId) return;
    const invRef = collection(db, 'companies', companyId, 'invoices');

    // Bounded OWED listener (canonical receivables). Ordered by document id
    // (default) so it needs NO composite index and works immediately; the
    // exact AR totals come from the aggregate below, independent of this list.
    const unsubOwed = onSnapshot(
      query(invRef, where('status', 'in', OWED_STATUSES), limit(OWED_PAGE_SIZE)),
      (snap) => {
        setOwedInvoices(snap.docs.map(d => ({ invoiceId: d.id, ...d.data() })));
        lastOwedDocRef.current = snap.docs[snap.docs.length - 1] || null;
        setHasMoreOwed(snap.docs.length === OWED_PAGE_SIZE);
        setLoading(false);
        loadArAggregate(companyId);
        loadAgingAggregates(companyId);
      },
      (err) => { console.error('[Invoices] owed listener error:', err); setLoading(false); }
    );

    // Recent history window — feeds the Paid/history section only.
    const unsubRecent = onSnapshot(
      query(invRef, orderBy('createdAt', 'desc'), limit(INVOICES_PAGE_SIZE)),
      (snap) => {
        setInvoices(snap.docs.map(d => ({ invoiceId: d.id, ...d.data() })));
        lastHistoryDocRef.current = snap.docs[snap.docs.length - 1] || null;
        setHasMoreHistory(snap.docs.length === INVOICES_PAGE_SIZE);
      },
      (err) => { console.error('[Invoices] history listener error:', err); }
    );

    return () => { unsubOwed(); unsubRecent(); };
  }, [companyId]);

  const loadMoreOwed = async () => {
    if (!companyId || !lastOwedDocRef.current || loadingMoreOwed) return;
    setLoadingMoreOwed(true);
    try {
      const snap = await getDocs(query(
        collection(db, 'companies', companyId, 'invoices'),
        where('status', 'in', OWED_STATUSES),
        startAfter(lastOwedDocRef.current),
        limit(OWED_PAGE_SIZE),
      ));
      setOlderOwed(prev => {
        const seen = new Set(prev.map(i => i.invoiceId));
        return [...prev, ...snap.docs.map(d => ({ invoiceId: d.id, ...d.data() })).filter(i => !seen.has(i.invoiceId))];
      });
      if (snap.docs.length) lastOwedDocRef.current = snap.docs[snap.docs.length - 1];
      setHasMoreOwed(snap.docs.length === OWED_PAGE_SIZE);
    } catch (err) {
      console.error('[Invoices] load more owed error:', err);
    } finally {
      setLoadingMoreOwed(false);
    }
  };

  const loadOlderInvoices = async () => {
    if (!companyId || !lastHistoryDocRef.current || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const snap = await getDocs(query(
        collection(db, 'companies', companyId, 'invoices'),
        orderBy('createdAt', 'desc'),
        startAfter(lastHistoryDocRef.current),
        limit(INVOICES_PAGE_SIZE),
      ));
      setOlderInvoices(prev => {
        const seen = new Set(prev.map(i => i.invoiceId));
        const added = snap.docs
          .map(d => ({ invoiceId: d.id, ...d.data() }))
          .filter(i => !seen.has(i.invoiceId));
        return [...prev, ...added];
      });
      if (snap.docs.length) lastHistoryDocRef.current = snap.docs[snap.docs.length - 1];
      setHasMoreHistory(snap.docs.length === INVOICES_PAGE_SIZE);
    } catch (err) {
      console.error('[Invoices] load older error:', err);
    } finally {
      setLoadingOlder(false);
    }
  };

  // Outstanding = canonical OWED set (bounded + on-demand more). Defensive
  // isOutstanding filter guarantees only sent/draft/failed_checkout appear.
  const outstanding = useMemo(() => {
    const map = new Map(owedInvoices.map(i => [i.invoiceId, i]));
    olderOwed.forEach(i => { if (!map.has(i.invoiceId)) map.set(i.invoiceId, i); });
    return [...map.values()].filter(isOutstanding);
  }, [owedInvoices, olderOwed]);

  // Paid/history = settled invoices from the recent window + on-demand older.
  // Each row keeps its ACTUAL status label (paid / refunded / partially_refunded)
  // via StatusBadge — grouped here as "history", never relabelled as Paid.
  const paid = useMemo(() => {
    const map = new Map();
    [...invoices, ...olderInvoices].forEach(i => {
      const s = (i.status || '').toLowerCase();
      if (s === 'paid' || s === 'refunded' || s === 'partially_refunded') map.set(i.invoiceId, i);
    });
    return [...map.values()];
  }, [invoices, olderInvoices]);

  // All = bounded mixed history (every status) from the recent-history listener
  // + on-demand older. Already createdAt-desc from Firestore; dedup preserves order.
  const allInvoices = useMemo(() => {
    const map = new Map();
    [...invoices, ...olderInvoices].forEach(i => { if (!map.has(i.invoiceId)) map.set(i.invoiceId, i); });
    return [...map.values()];
  }, [invoices, olderInvoices]);

  // Loaded aging map — used ONLY to filter the bounded visible working list
  // when an operator clicks a bucket in AgingStrip. The DISPLAYED bucket
  // totals/counts come from the exact server aggregates (agingAgg), not this.
  const aging = useMemo(() => {
    const current = [], d30 = [], d60 = [], d90 = [];
    outstanding.forEach(i => {
      const days = daysOld(i.createdAt);
      if      (days >= 90) d90.push(i);
      else if (days >= 60) d60.push(i);
      else if (days >= 30) d30.push(i);
      else                 current.push(i);
    });
    return { current, d30, d60, d90 };
  }, [outstanding]);

  // Filtered outstanding — respects the aging bucket clicked in AgingStrip,
  // OR the attention filter (Dashboard deep-link OR the in-view toggle), OR the
  // Phase E "skipped only" opt-in filter.
  const isAttentionInvoice = (i) =>
    i.status === 'failed_checkout' || i.checkoutCreationFailed === true || i.autoChargeFailed === true;
  const attentionActive = attentionOnly || attentionToggle;
  const attentionCount = useMemo(
    () => outstanding.filter(isAttentionInvoice).length,
    [outstanding],
  );
  const filteredOutstanding = useMemo(() => {
    let list = outstanding;
    if (attentionActive) list = list.filter(isAttentionInvoice);
    if (skippedOnly) {
      list = list.filter(i => i.autoChargeSkipped === true);
    }
    if (agingFilter) list = aging[agingFilter] ?? list;
    return list;
  }, [agingFilter, outstanding, aging, attentionActive, skippedOnly]);

  // Summary metrics — EXACT business-wide receivable from the server aggregate.
  // Count is exact (single-field index). Dollars come ONLY from the sum
  // aggregate; when its (status, amount) index is not yet deployed we report
  // the dollars as UNAVAILABLE rather than substitute a partial loaded subtotal.
  const outstandingCount = arTotal.count;                       // exact | null while loading
  const totalOutstanding = arTotal.sum;                         // exact $ | null if unavailable
  const sumUnavailable = arTotal.loaded && arTotal.sum == null; // index pending

  // Reconciliation guardrail — if the exact company-wide total exceeds the sum
  // of the four aging buckets, one or more owed invoices lack a valid Timestamp
  // createdAt and fell outside every range. Surface that residual truthfully
  // ("N invoices · $X unaged") instead of silently dropping receivables from
  // the aged view. For canonical forward records this residual is normally 0.
  const agingResidual = useMemo(
    () => reconcileAgingResidual(arTotal, agingAgg.ok ? agingAgg.buckets : null),
    [agingAgg, arTotal],
  );

  const paidThisMonth = useMemo(
    () => paid
      .filter(i => isThisMonth(i.paidAt || i.createdAt))
      .reduce((sum, i) => sum + parseFloat(i.amount || 0), 0),
    [paid]
  );

  // ── Actions ────────────────────────────────────────────────────────────────

  const handleCopyLink = (invoice) => {
    if (!invoice.stripePaymentLink) {
      toast.error('No payment link available');
      return;
    }
    navigator.clipboard.writeText(invoice.stripePaymentLink)
      .then(() => toast.success('Payment link copied to clipboard'))
      .catch(() => toast.error('Failed to copy link'));
  };

  const handleSendReminder = async (invoice) => {
    if (!invoice.stripePaymentLink) {
      toast.error('No payment link on this invoice');
      return;
    }
    setSending(invoice.invoiceId);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = await auth.currentUser?.getIdToken(false);
      const res = await fetch(`${backendUrl}/api/invoices/${invoice.invoiceId}/send-reminder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ companyId }),
      });
      let data = {};
      try {
        data = await res.json();
      } catch (_jsonErr) {
        // Response body may have been consumed by analytics interceptors (e.g. PostHog).
        // Fall back to HTTP status for basic success/error detection.
        data = res.ok
          ? { success: true, message: 'Reminder sent.' }
          : { success: false, detail: `Request failed with status ${res.status}` };
      }
      if (data.success) {
        toast.success('Reminder sent.');
      } else {
        // Show backend detail (e.g. rate-limit message) if available
        const detail = data.detail || data.message || 'Failed to send reminder';
        toast.error(detail);
      }
    } catch (err) {
      console.error('[Invoices] Send reminder error:', err);
      toast.error('Failed to send reminder');
    } finally {
      setSending(null);
    }
  };

  const handleRetryCharge = async (invoice) => {    setRetrying(invoice.invoiceId);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = await auth.currentUser?.getIdToken(false);
      const res = await fetch(`${backendUrl}/api/payments/charge-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ companyId, invoiceId: invoice.invoiceId }),
      });
      let data = {};
      try { data = await res.json(); } catch (_) {}

      if (res.ok && data.success) {
        const brand = data.cardBrand || 'Card';
        const last4 = data.cardLast4 || '****';
        const amt   = parseFloat(data.amount || 0).toFixed(2);
        toast.success(`Charged ${brand} ••••${last4} — $${amt} collected`);
        // Firestore real-time listener auto-moves invoice from Outstanding → Paid
      } else {
        const detail = data.detail || 'Charge failed. Check card on file.';
        toast.error(detail);
      }
    } catch (err) {
      console.error('[Invoices] Retry charge error:', err);
      toast.error('Network error — please try again');
    } finally {
      setRetrying(null);
    }
  };

  const handleRetryInvoice = async (invoice) => {
    setRetryingInvoice(invoice.invoiceId);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = await auth.currentUser?.getIdToken(false);
      const res = await fetch(`${backendUrl}/api/invoices/${invoice.invoiceId}/retry-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ companyId, originUrl: window.location.origin }),
      });
      let data = {};
      try { data = await res.json(); } catch (_) { /* ignore */ }
      if (res.ok && data.success) {
        toast.success('Payment link recreated — invoice moved to Sent');
        // Firestore listener updates the row in place
      } else if (res.status === 409) {
        toast.info(data.detail || 'Invoice is no longer in a retryable state.');
      } else {
        toast.error(data.detail || 'Retry failed. Please try again.');
      }
    } catch (err) {
      console.error('[Invoices] Retry invoice error:', err);
      toast.error('Network error — please try again');
    } finally {
      setRetryingInvoice(null);
    }
  };


  // ── Render ────────────────────────────────────────────────────────────────

  // Shared row-action props for whichever view's InvoiceSection is mounted.
  const sectionActionProps = {
    onCopyLink: handleCopyLink,
    onSendReminder: handleSendReminder,
    sending,
    onRetryCharge: handleRetryCharge,
    retrying,
    onRetryInvoice: handleRetryInvoice,
    retryingInvoice,
    onChargeNow: setChargeNowInvoice,
    onMarkPaid: setMarkPaidInvoice,
    onRefund: setRefundInvoice,
    canMarkPaid,
  };

  // ── Bounded pagination (Invoices P2 revision) ───────────────────────────────
  // The active tab exposes its full loaded list, the exact total (when known),
  // and whether Firestore has MORE beyond the loaded buffer. We render only the
  // current 25-row slice; Next fetches the next cursor page when the buffer runs
  // out, Previous is instant from the buffered rows.
  const filterActive = view === 'outstanding' && !!(agingFilter || attentionActive || skippedOnly);
  const activeList =
    view === 'outstanding' ? filteredOutstanding
    : view === 'paid'      ? paid
    :                        allInvoices;
  // Exact total is only known for the unfiltered Outstanding tab (server count()).
  const activeTotal = (view === 'outstanding' && !filterActive) ? outstandingCount : null;
  const serverHasMore =
    view === 'outstanding' ? (hasMoreOwed && !filterActive)
    :                        hasMoreHistory;
  const pageLoading = view === 'outstanding' ? loadingMoreOwed : loadingOlder;

  // Reset to the first page whenever the tab or an Outstanding filter changes.
  useEffect(() => {
    setPage(0);
  }, [view, agingFilter, attentionActive, skippedOnly]);

  const renderedRows = activeList.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const hasPrevPage = page > 0;
  const hasNextPage = activeList.length > (page + 1) * PAGE_SIZE || serverHasMore;

  const goPrevPage = () => setPage(p => Math.max(0, p - 1));
  const goNextPage = async () => {
    const firstIdxOfNext = (page + 1) * PAGE_SIZE;
    // Fetch the next Firestore cursor page if the buffer can't cover the next page.
    if (activeList.length <= firstIdxOfNext && serverHasMore) {
      if (view === 'outstanding') await loadMoreOwed();
      else await loadOlderInvoices();
    }
    setPage(p => p + 1);
  };

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-5" data-testid="invoices-page">

      {/* Header */}
      <div className="flex items-center gap-3">
        <Receipt className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold text-secondary">Invoices</h1>
          <p className="text-sm text-muted-foreground">{outstandingCount ?? '…'} outstanding · recent history shown</p>
        </div>
      </div>

      {/* Summary metrics */}
      <div className="grid grid-cols-2 gap-3" data-testid="invoices-metrics">
        <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
            <Clock className="h-4 w-4 text-amber-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500">Outstanding</p>
            <p className="text-xl font-bold text-gray-900" data-testid="metric-outstanding">
              {totalOutstanding != null ? fmt$(totalOutstanding) : (sumUnavailable ? '$—' : '…')}
            </p>
            {totalOutstanding == null && sumUnavailable ? (
              <p className="text-[11px] text-amber-600" data-testid="metric-outstanding-unavailable">
                Total unavailable — index pending
              </p>
            ) : (
              <p className="text-[11px] text-gray-400">
                across {outstandingCount ?? '…'} invoice{outstandingCount === 1 ? '' : 's'}
              </p>
            )}
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-green-100 flex items-center justify-center shrink-0">
            <DollarSign className="h-4 w-4 text-green-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500">Paid This Month</p>
            <p className="text-xl font-bold text-gray-900" data-testid="metric-paid-this-month">
              {fmt$(paidThisMonth)}
            </p>
            <p className="text-[11px] text-gray-400">{paid.filter(i => isThisMonth(i.paidAt || i.createdAt)).length} payment{paid.filter(i => isThisMonth(i.paidAt || i.createdAt)).length !== 1 ? 's' : ''} this month</p>
          </div>
        </div>
      </div>

      {/* Attention-only filter chip (from Dashboard deep-link) */}
      {attentionOnly && (
        <div
          className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          data-testid="attention-filter-chip"
        >
          <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
          <p className="text-sm text-red-900 flex-1">
            Showing only invoices that need follow-up — failed charges or missing payment links.
          </p>
          <button
            type="button"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete('filter');
              setSearchParams(next, { replace: true });
            }}
            className="flex items-center gap-1 text-xs font-medium text-red-700 hover:underline"
            data-testid="attention-filter-clear"
          >
            <X className="h-3 w-3" />
            Clear filter
          </button>
        </div>
      )}

      {/* Aging breakdown — exact server aggregates; visible when receivables exist */}
      {((outstandingCount ?? outstanding.length) > 0) && !loading && (
        <AgingStrip
          buckets={agingAgg.buckets}
          available={agingAgg.ok}
          loaded={agingAgg.loaded}
          residual={agingResidual}
          activeFilter={agingFilter}
          onBucketClick={(key) => { setView('outstanding'); setAgingFilter(prev => prev === key ? null : key); }}
        />
      )}

      {/* View tabs (Invoices P2) — Outstanding | Paid | All. Only the active
          view renders, so Paid history never stacks below Outstanding. */}
      {!loading && (
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit" data-testid="invoice-view-tabs">
          {[
            { key: 'outstanding', label: 'Outstanding', badge: outstandingCount },
            { key: 'paid', label: 'Paid', badge: null },
            { key: 'all', label: 'All', badge: null },
          ].map(t => (
            <button
              key={t.key}
              type="button"
              data-testid={`invoice-tab-${t.key}`}
              onClick={() => setView(t.key)}
              className={`px-3.5 py-1.5 text-sm font-medium rounded-md transition-colors ${
                view === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
              {t.badge != null && (
                <span className={`ml-1.5 text-[11px] rounded-full px-1.5 py-0.5 ${
                  view === t.key ? 'bg-amber-100 text-amber-700' : 'bg-gray-200 text-gray-500'
                }`}>
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Outstanding-only filter chips: Needs Attention + Skipped */}
      {!loading && view === 'outstanding' &&
        (attentionCount > 0 || outstanding.some(i => i.autoChargeSkipped === true)) && (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="outstanding-filter-chips">
          {attentionCount > 0 && (
            <button
              type="button"
              onClick={() => setAttentionToggle(v => !v)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-colors ${
                attentionActive
                  ? 'border-red-300 bg-red-50 text-red-800'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-red-50/40'
              }`}
              data-testid="outstanding-attention-toggle"
              title="Filter to failed charges / missing payment links that need intervention"
            >
              <AlertCircle className="h-3 w-3" />
              {attentionActive ? 'Showing needs attention' : `Needs attention (${attentionCount})`}
            </button>
          )}
          {outstanding.some(i => i.autoChargeSkipped === true) && (
            <button
              type="button"
              onClick={() => setSkippedOnly(v => !v)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-colors ${
                skippedOnly
                  ? 'border-amber-300 bg-amber-50 text-amber-800'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-amber-50/40'
              }`}
              data-testid="invoices-skipped-filter-toggle"
              title="Filter to invoices whose auto-charge was skipped by the lifecycle gate"
            >
              <AlertTriangle className="h-3 w-3" />
              {skippedOnly ? 'Showing skipped only' : `Show skipped (${outstanding.filter(i => i.autoChargeSkipped === true).length})`}
            </button>
          )}
          {(attentionToggle || skippedOnly) && (
            <button
              type="button"
              onClick={() => { setAttentionToggle(false); setSkippedOnly(false); }}
              className="text-gray-500 hover:underline"
              data-testid="outstanding-filter-clear"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* Invoice table — single active view (Invoices P2) */}
      {loading ? (
        <div className="text-center py-16 text-gray-400 text-sm" data-testid="invoices-loading">
          Loading invoices…
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid={`invoice-view-${view}`}>
          {(() => {
            const cfg = view === 'paid'
              ? { title: 'Paid & Refunded', icon: CheckCircle2, iconCls: 'text-green-500', emptyMsg: 'No paid or refunded invoices in the recent history window.' }
              : view === 'all'
              ? { title: 'All Invoices', icon: Receipt, iconCls: 'text-gray-500', emptyMsg: 'No invoices yet. Invoices are created from completed jobs.' }
              : {
                  title: attentionActive
                    ? 'Needs Attention'
                    : (agingFilter ? `Outstanding · ${AGING_LABELS[agingFilter]}` : 'Outstanding'),
                  icon: Clock,
                  iconCls: 'text-amber-500',
                  emptyMsg: attentionActive
                    ? 'No invoices currently need follow-up.'
                    : (agingFilter ? `No invoices in the ${AGING_LABELS[agingFilter]} bucket.` : 'No outstanding invoices.'),
                };
            return (
              <>
                <InvoiceSection
                  title={cfg.title}
                  icon={cfg.icon}
                  iconCls={cfg.iconCls}
                  invoices={renderedRows}
                  count={activeTotal}
                  emptyMsg={cfg.emptyMsg}
                  {...sectionActionProps}
                />
                <PaginationBar
                  page={page}
                  pageSize={PAGE_SIZE}
                  renderedCount={renderedRows.length}
                  total={activeTotal}
                  hasNext={hasNextPage}
                  hasPrev={hasPrevPage}
                  onPrev={goPrevPage}
                  onNext={goNextPage}
                  loading={pageLoading}
                />
              </>
            );
          })()}
        </div>
      )}

      {/* Charge Now Modal */}
      <ChargeNowModal
        isOpen={!!chargeNowInvoice}
        onClose={() => setChargeNowInvoice(null)}
        companyId={companyId}
        leadId={chargeNowInvoice?.leadId}
        customerName={chargeNowInvoice?.customerName}
        invoice={chargeNowInvoice}
        onSuccess={() => setChargeNowInvoice(null)}
      />

      {/* Mark Paid Modal (Billing Stabilization Step 2, iter_221) */}
      <MarkPaidModal
        isOpen={!!markPaidInvoice}
        onClose={() => setMarkPaidInvoice(null)}
        companyId={companyId}
        invoice={markPaidInvoice}
        onSuccess={() => setMarkPaidInvoice(null)}
      />

      {/* Refund Modal (Billing Stabilization Step 3, iter_222) */}
      <RefundModal
        isOpen={!!refundInvoice}
        onClose={() => setRefundInvoice(null)}
        companyId={companyId}
        invoice={refundInvoice}
        onSuccess={() => setRefundInvoice(null)}
      />
    </div>
  );
};

export default Invoices;
